import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { User, Menu, X, ChevronDown, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { logoutUser } from '../store/userSlice';
import LoginModal from './LoginModal';
import LanguageSwitcher from './LanguageSwitcher';
import { SESSION_EXPIRED_EVENT, TOKEN_REFRESH_FAILED_EVENT } from '../api/authenticatedRequest';
import AccountRefreshNotice from './AccountRefreshNotice';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showRefreshNotice, setShowRefreshNotice] = useState(false);
  
  // 使用国际化
  const { t } = useTranslation();
  
  // 使用Redux状态替代本地用户状态
  const dispatch = useDispatch();
  const { user, isLoggedIn } = useSelector((state) => state.user);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowRefreshNotice(false);
      dispatch(logoutUser());
      setIsMenuOpen(false);
      setIsUserMenuOpen(false);
      navigate('/', { replace: true });
      setIsLoginModalOpen(true);
    };
    const handleRefreshFailed = () => {
      setIsMenuOpen(false);
      setIsUserMenuOpen(false);
      setShowRefreshNotice(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    window.addEventListener(TOKEN_REFRESH_FAILED_EVENT, handleRefreshFailed);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
      window.removeEventListener(TOKEN_REFRESH_FAILED_EVENT, handleRefreshFailed);
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  // 导航菜单项
  const navItems = [
    { name: '升级教程', href: '/upgrade-guide' },
    { name: '防封指南', href: '/anti-ban-guide' },
    { name: '客服支持', href: '/customer-service' },
    { name: t('navigation.orders', '订单历史'), href: '/orders' },
    // { name: '售后服务', href: '/after-sales' }
  ];

  // 处理登录成功 - 现在由Redux管理，不需要本地处理
  const handleLoginSuccess = () => {
    setShowRefreshNotice(false);
    setIsLoginModalOpen(false);
  };

  // 处理登出 - 使用Redux action
  const handleLogout = () => {
    dispatch(logoutUser());
    setIsUserMenuOpen(false);
  };

  // 检查当前路径是否匹配
  const isActiveRoute = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // 关闭移动端菜单
  const closeMobileMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className="site-header bg-white border-b border-[var(--site-border)] sticky top-0 z-50 shadow-sm">
      {/* 单行Header - 合并语言货币选择和主导航 */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          
          {/* 左侧 - Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link
                to="/"
                className="flex items-center hover:opacity-80 transition-opacity duration-300"
              >
                <img src="/images/logo.jpg" alt="大师兄 Logo" width="44" height="44" className="w-10 h-10 sm:w-11 sm:h-11 object-contain mr-2 sm:mr-3" />
                <div className="hidden sm:block">
                  <p className="text-lg sm:text-xl font-bold text-[var(--site-ink)]">{t('header.brandName')}</p>
                  <p className="text-xs text-[var(--site-muted)] hidden lg:block">{t('header.brandSlogan')}</p>
                </div>
                <div className="sm:hidden">
                  <p className="text-base font-bold text-[var(--site-ink)]">{t('header.brandName')}</p>
                </div>
              </Link>
            </div>
          </div>

          {/* 中间 - 桌面端导航 */}
          <nav className="hidden lg:block flex-1 mx-4">
            <div className="flex items-center justify-center space-x-2 xl:space-x-6">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`px-3 xl:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    isActiveRoute(item.href)
                      ? 'bg-[var(--site-brand-soft)] text-[var(--site-brand)]'
                      : 'text-gray-700 hover:text-[var(--site-ink)] hover:bg-[var(--site-section)]'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>

          {/* 右侧 - 语言货币 + 用户登录 + 移动端菜单 */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            
            {/* 货币显示和语言切换器组合 */}
            <div className="hidden sm:flex items-center space-x-2 lg:space-x-3">
              {/* 货币显示 - 固定美元 */}
              <div className="hidden xl:flex items-center space-x-1 text-sm text-[var(--site-muted)]">
                <span aria-hidden="true">¥</span>
                <span>CNY</span>
              </div>
              
              {/* 分隔线 */}
              <div className="hidden xl:block h-5 w-px bg-gray-300"></div>
              
              {/* 语言选择器 - 自定义样式以适应Header */}
              <div className="language-switcher-header">
                <LanguageSwitcher className="header-language-switcher" />
              </div>
            </div>
            
            {/* 用户登录状态 */}
            {isLoggedIn && user ? (
              // 已登录状态
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="hidden sm:flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg text-gray-700 hover:text-[var(--site-ink)] hover:bg-[var(--site-section)] transition-all duration-300"
                >
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-200 rounded-full flex items-center justify-center border border-gray-300">
                    <span className="text-[var(--site-ink)] text-xs sm:text-sm font-semibold">
                      {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                  <span className="text-sm font-medium hidden md:inline-block max-w-[100px] lg:max-w-[150px] truncate">{user.name || user.email || t('common.user')}</span>
                  <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              
                {/* 用户菜单 */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-[var(--site-border)] py-2 z-50">
                    <Link
                      to="/settings"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-[var(--site-section)] hover:text-[var(--site-ink)] transition-colors duration-300"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4 mr-3" />
                      {t('header.userMenu.settings', '设置')}
                    </Link>
                    <div className="border-t border-[var(--site-border)] my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors duration-300"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      {t('header.userMenu.logout')}
                    </button>
                </div>
              )}
            </div>
            ) : (
              // 未登录状态
              <button 
                onClick={() => setIsLoginModalOpen(true)}
                className="hidden sm:flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 rounded-lg text-gray-700 hover:text-[var(--site-ink)] hover:bg-[var(--site-section)] transition-all duration-300"
            >
              <User className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm font-medium hidden md:inline-block">{t('navigation.login')}</span>
              </button>
            )}

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? '关闭导航菜单' : '打开导航菜单'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
              className="lg:hidden min-w-[44px] min-h-[44px] flex items-center justify-center p-2 rounded-lg text-gray-700 hover:text-[var(--site-ink)] hover:bg-[var(--site-section)] transition-all duration-300"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {isMenuOpen && (
        <div id="mobile-navigation" className="site-mobile-navigation lg:hidden bg-[var(--site-section)] border-t border-[var(--site-border)]">
          <div className="px-2 pt-2 pb-3 space-y-1">
            
            {/* 移动端语言和货币显示 */}
            <div className="px-3 py-2 sm:py-3 border-b border-[var(--site-border)] mb-2">
              <div className="flex items-center justify-between">
                <LanguageSwitcher className="mobile-header" />
                <div className="flex items-center space-x-1 text-[var(--site-muted)]">
                  <span aria-hidden="true">¥</span>
                  <span className="text-sm">CNY</span>
                </div>
              </div>
            </div>
            
            {/* 移动端导航菜单 */}
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={closeMobileMenu}
                className={`block px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 ${
                  isActiveRoute(item.href)
                    ? 'bg-[var(--site-brand-soft)] text-[var(--site-brand)]'
                    : 'text-gray-700 hover:text-[var(--site-ink)] hover:bg-[var(--site-section)]'
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* 移动端登录 */}
            <div className="border-t border-[var(--site-border)] pt-3 mt-3">
              {isLoggedIn && user ? (
                <>
                  <div className="flex items-center px-3 py-2 text-[var(--site-ink)]">
                    <div className="w-8 h-8 shrink-0 bg-gray-200 rounded-full flex items-center justify-center mr-3 border border-gray-300">
                      <span className="text-[var(--site-ink)] text-sm font-semibold">
                        {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <span className="font-medium min-w-0 break-all">{user.name || user.email || t('common.user')}</span>
                  </div>
                  <Link to="/settings" onClick={closeMobileMenu} className="flex items-center px-3 py-2 text-gray-700 rounded-lg hover:bg-white">
                    <User className="h-5 w-5 mr-3" aria-hidden="true" />账号设置
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-300 w-full"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    {t('navigation.logout')}
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => {
                    setIsLoginModalOpen(true);
                    closeMobileMenu();
                  }}
                  className="flex items-center px-3 py-2 text-gray-700 hover:text-[var(--site-ink)] hover:bg-[var(--site-section)] rounded-lg transition-all duration-300 w-full"
              >
                <User className="h-5 w-5 mr-3" />
                {t('navigation.loginRegister')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 点击外部关闭下拉菜单 */}
      {isUserMenuOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsUserMenuOpen(false);
          }}
        />
      )}

      {/* 登录弹窗 */}
      {showRefreshNotice && (
        <AccountRefreshNotice
          onClose={() => setShowRefreshNotice(false)}
          onLogin={() => {
            setShowRefreshNotice(false);
            setIsLoginModalOpen(true);
          }}
        />
      )}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLogin={handleLoginSuccess}
      />
    </header>
  );
}
